import _ from '@lodash';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Switch, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Checkbox, ListItemText, ListSubheader, IconButton, Icon } from '@material-ui/core';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useInterval } from 'react-timers-hooks';
import StartStreamingCommand from 'app/protocol/streaming/StartStreamingCommand';
import StopStreamingCommand from 'app/protocol/streaming/StopStreamingCommand';
import { useSipWebsocket } from 'app/protocol/SipWebsocketProvider';
import DeviceService from '../service/DeviceService';
import StreamDataParser from './StreamDataParser';
import CsvDataParser from './CsvDataParser';

const dataLength = 25;
const _tick = 20;
export const colorNames = [
	'Blue', 'Red', 'Yellow', 'Green', 'Purple', 
	'Orange', 'Cyan', 'Magenta', 'Lime', 'Pink', 
	'Teal', 'Lavender', 'Brown', 'Beige', 'Maroon', 
	'Mint', 'Olive', 'Coral', 'Navy', 'Grey'
];

function DeviceStreaming(props) {
	const dispatch = useDispatch();
	const { sipId, initialized, sendMessage } = useSipWebsocket();
	const [device, setDevice] = useState(null);
	const [selectedSensorList, setSelectedSensorList] = useState([]);
	const [selectedValueList, setSelectedValueList] = useState([]);
	const [valueList, setValueList] = useState([]);
	const [running, setRunning] = useState(false);
	const [chartHeight, setChartHeight] = useState(0);
	const controlsRef = useRef(null);
	const resizeObserverRef = useRef(null);
	const [data, setData] = useState([]);
	const [lines, setLines] = useState([]);
	const [tick, setTick] = useState(_tick);
	const [csvUrl, setCsvUrl] = useState(null);
	const [csvName, setCsvName] = useState(null);
	const mounted = useRef(true);

	useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	useEffect(() => {
		resizeObserverRef.current = new ResizeObserver(entries => {
			updateChartHeight();
		});
		function handleWindowResize() {
			updateChartHeight();
		}
		window.addEventListener('resize', handleWindowResize);

		return () => {
			console.log('end');
			if (resizeObserverRef.current && controlsRef.current) resizeObserverRef.current.unobserve(controlsRef.current);
			window.removeEventListener('resize', handleWindowResize);
		};
	}, []);

	useEffect(() => {
		if (controlsRef.current) {
			resizeObserverRef.current.observe(controlsRef.current);
			updateChartHeight();
		}
	}, [controlsRef.current]);

	useEffect(() => {
		if (props.fieldConfig.id) {
			DeviceService.getData(props.fieldConfig.id).then(resp => {
				if (resp?.deviceType?.sensorTypeList) {
					const sensorList = resp.deviceType.sensorTypeList
						.filter(d => d.modelId !== 'Ticks')
						.map(d => d.externalId);
					
					const newValueList = [];
					resp.deviceType.sensorTypeList.forEach(s => 
						s.valueList.forEach(v => newValueList.push(v.externalId))
					);
					
					setSelectedSensorList(sensorList);
					setSelectedValueList(newValueList);
				} else {
					setSelectedSensorList([]);
					setSelectedValueList([]);
				}
				setDevice(resp);
			});
		}
	}, [props.fieldConfig.id]);

	const updateChartHeight = () => {
		if (controlsRef.current) {
			const rect = controlsRef.current.getBoundingClientRect();
			const newHeight = window.innerHeight - 100 - rect.top + window.scrollY - rect.height;
			setChartHeight(newHeight);
		}
	};

	const handleSensorChange = e => {
		const newSensorList = e.target.value;
		setSelectedSensorList(newSensorList);
	};

	const handleSensorValueChange = e => {
		const newValueList = e.target.value;
		setSelectedValueList(newValueList);
	};

	useEffect(() => {
		if (device) {
			const vList = [];
			if (device && device.deviceType && device.deviceType.sensorTypeList)
				device.deviceType.sensorTypeList
					.filter(d => selectedSensorList.findIndex(v => v === d.externalId) > -1)
					.forEach(s => {
						vList.push(s);
						s.valueList.forEach(v => {
							v.value = v.externalId;
							vList.push(v);
						});
					});
			setValueList(vList);
		}
	}, [selectedSensorList, device]);

	const collectSelectedValueLabel = selected => {
		return device.deviceType.sensorTypeList
			.filter(d => selectedSensorList.findIndex(s => s === d.externalId) > -1)
			.map(
				sensor =>
					`${sensor.modelId}: ${sensor.valueList
						.filter(d => selected.findIndex(s => d.externalId === s) > -1)
						.map(d => d.name)
						.join(', ')}`
			)
			.join(', ');
	};

	useEffect(() => {
		if (running && sipId) {
			//console.log(`start message count: ${messageCountRef.current}`);
			sendMessage(new StartStreamingCommand(sipId, device.externalId, 0, 0));
		}
	}, [sipId, running]);

	useEffect(() => {
		if (running) {
			if (csvUrl !== null) window.URL.revokeObjectURL(csvUrl);
			setCsvUrl(null);
			setCsvName(null);
			setLines([]);
			setData([]);
			setTick(_tick);
		}
		return () => {
			if (running) {
				sendMessage(new StopStreamingCommand(sipId, device.externalId));
			}
		};
	}, [running]);

	useInterval(
		() => {
			setTick(tick - 1);
			if (tick === 1) {
				dispatch(showMessage({ message: 'Device connection failed.' }));
				setRunning(false);
			}
		},
		running && lines.length === 0 && tick > 0 ? 1000 : null
	);

	const initLines = sensorTypes => {
		const _lines = [];
		let pos = 0;
		sensorTypes.forEach(sensorType => {
			const prefix = `s${sensorType.typeId}`;
			sensorType.sensorValues.forEach(sensorValue => {
				_lines.push({
					key: `v${pos}`,
					label: findSensorValueLabel(sensorType.typeId, sensorValue.typeId) || `${prefix}/v${sensorValue.typeId}`,
					color: colorNames[pos],
					sensorId: sensorType.typeId,
					valueId: sensorValue.typeId
				});
				pos += 1;
			});
		});
		const _data = [];
		for (let i = 0; i < dataLength; i += 1) {
			const dataObj = {
				name: i
			};
			_lines.forEach(line => {
				dataObj[line.key] = null;
			});
			_data.push(dataObj);
		}
		setLines(_lines);
		setData(_data);
	};

	const addData = currentData => {
		let dataPos = dataLength;
		const dataPosIndex = data.findIndex(d => d.v0 === null);
		if (dataPosIndex > -1) {
			dataPos = data[dataPosIndex].name;
		}
		const dataObj = {
			name: dataPos
		};
		currentData.forEach((d, i) => {
			dataObj[`v${i}`] = d.data;
		});
		if (dataPos < dataLength) {
			const _data = data.map(d => (d.name === dataPos ? dataObj : d));
			setData(_data);
		} else {
			const _data = data.filter(d => d.name > 0);
			_data.push(dataObj);
			setData(
				_data.map(d => {
					d.name -= 1;
					return d;
				})
			);
		}
	};

	const findSensorValueLabel = (sensorId, valueId) => {
		const sensor = device.deviceType.sensorTypeList.find(s => s.externalId === sensorId);
		if (sensor) {
			const value = sensor.valueList.find(v => v.externalId === valueId);
			if (value) return `${sensor.modelId}: ${value.name}`;
		}
		return null;
	};

	const updateDevice = async () => {
		if (mounted.current) {
			try {
				const response = await DeviceService.getData(props.id);
				if (mounted.current) {
					setDevice(response);
				}
			} catch (error) {
				console.error('Failed to fetch device:', error);
			}
		}
	};

	useEffect(() => {
		if (props.id) {
			updateDevice();
		}
	}, [props.id]);

	return device && device.deviceType ? (
		<div>
			<div ref={controlsRef} className="flex flex-row">
				<FormControl className="flex-1 mt-8 mb-16 mr-12" variant="outlined">
					<InputLabel id="sensors-label">Sensors</InputLabel>
					<Select
						id="sensors"
						labelId="sensors-label"
						label="Sensors"
						multiple
						value={selectedSensorList || []}
						onChange={handleSensorChange}
						renderValue={selected =>
							device.deviceType.sensorTypeList
								.filter(d => selected.indexOf(d.externalId) > -1)
								.map(d => d.modelId)
								.join(', ')
						}
					>
						{device.deviceType.sensorTypeList.map(sensor => (
							<MenuItem key={sensor.externalId} value={sensor.externalId}>
								<Checkbox checked={selectedSensorList.indexOf(sensor.externalId) > -1} />
								<ListItemText primary={sensor.modelId} />
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl className="flex-1 mt-8 mb-16 mr-12" variant="outlined">
					<InputLabel id="values-label">Values</InputLabel>
					<Select 
						id="values" 
						labelId="values-label" 
						label="Values" 
						multiple 
						value={selectedValueList || []}
						onChange={handleSensorValueChange}
						renderValue={collectSelectedValueLabel}
					>
						{valueList.map((d, i) =>
							d.value ? (
								<MenuItem key={i} value={d.value}>
									<Checkbox checked={selectedValueList.indexOf(d.value) > -1} />
									<ListItemText primary={d.name} />
								</MenuItem>
							) : (
								<ListSubheader key={i}>{d.modelId}</ListSubheader>
							)
						)}
					</Select>
				</FormControl>
				<FormControlLabel control={<Switch checked={running} onChange={() => setRunning(!running)} />} disabled={!initialized} label={running ? 'Stop' : 'Start'} />
				<IconButton className="mt-16 mb-16 mr-12" disabled={csvUrl === null && csvName === null} component="a" href={csvUrl} download={csvName}>
					<Icon fontSize="small">download</Icon>
				</IconButton>
			</div>
			<div className="w-full" style={{ height: chartHeight }}>
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={data}
						margin={{
							top: 0,
							right: 0,
							left: 0,
							bottom: 0
						}}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="name" />
						<YAxis />
						<Tooltip />
						<Legend />
						{lines.map((line, index) =>
							selectedSensorList.indexOf(line.sensorId) > -1 && selectedValueList.indexOf(line.valueId) > -1 ? (
								<Line key={line.key} type="monotone" dataKey={line.key} stroke={colorNames[index % colorNames.length]} name={line.label} activeDot={{ r: 8 }} isAnimationActive={false} />
							) : null
						)}
					</LineChart>
				</ResponsiveContainer>
			</div>
			<StreamDataParser running={running} handleTypes={initLines} handleData={addData} lines={lines} />
			<CsvDataParser running={running} device={device} handleCsvName={setCsvName} handleCsvUrl={setCsvUrl} />
		</div>
	) : null;
}
export default DeviceStreaming;
